import {constants} from './constants';

/**
 * Dizionario delle voci di menù.
 *
 * Dichiara quali voci ESISTONO (id, etichetta, sezione, ordine) e il loro binding di DEFAULT
 * (modulo che le sblocca + ruoli che le vedono). I default riproducono letteralmente le condizioni
 * che erano inline in app.component.html: a database vuoto il menù si comporta esattamente come prima.
 *
 * Il binding è sovrascrivibile a runtime dalla tabella globale MenuVociBindings (pagina /licenze).
 * Riga assente = si applica il default dichiarato qui.
 *
 * Aggiungere una voce di menù significa: aggiungere il markup in app.component.html E una voce qui.
 * Gli `id` sono chiavi persistite a database: non vanno mai riusati per una voce diversa.
 */
export interface MenuVoce {
  /** Identificativo stabile, persistito. */
  readonly id: string;
  /** Etichetta mostrata nelle UI di configurazione (non nel menù, che ha il proprio markup). */
  readonly label: string;
  /** Sezione di appartenenza; assente sulle sezioni stesse. */
  readonly sezioneId?: string;
  /** True per gli `ion-list-header`: la visibilità dipende dalle voci figlie. */
  readonly isSezione?: boolean;
  /**
   * Classe Tailwind del colore titolo, presente solo sulle sezioni: è testualmente la stessa
   * classe usata in app.component.html per il relativo `ion-list-header`, non un'approssimazione.
   * Va aggiornata a mano se cambia lì — stesso principio di `label`.
   */
  readonly colorClass?: string;
  /** Codice modulo richiesto per default. Assente = nessun requisito di licenza. */
  readonly modulo?: string;
  /** Ruoli in OR richiesti per default. Assente = nessun requisito di ruolo. */
  readonly ruoli?: readonly string[];
  /** False = fuori dalle UI di configurazione (voce di servizio o d'amministrazione). */
  readonly configurabile?: boolean;
}

/**
 * Contiene solo le voci la cui visibilità dipende da licenza o ruolo. Dashboard, Accedi, Account,
 * Esci e il toggle del tema non hanno condizioni di autorizzazione e restano fuori dal dizionario.
 */
const MENU_VOCI_SORGENTE = [
  // ---- MES manuale ------------------------------------------------------------------------
  {id: 'sez-mes', label: 'MES manuale', isSezione: true, configurabile: false, colorClass: 'text-blue-500'},
  {
    id: 'mes-avanzamento', label: 'Avanzamento manuale', sezioneId: 'sez-mes',
    modulo: constants.MOD_PRO_BASE_MAN, ruoli: [constants.ROLE_MES]
  },
  {
    id: 'mes-trasferimento', label: 'Trasferimento materiale', sezioneId: 'sez-mes',
    modulo: constants.MOD_PRO_BASE_TRANSFER, ruoli: [constants.ROLE_TRANSFER]
  },

  // ---- Ordini di lavoro -------------------------------------------------------------------
  {id: 'sez-ordini', label: 'Ordini di lavoro', isSezione: true, configurabile: false, colorClass: 'text-blue-500'},
  {
    id: 'ordini-vista-raggruppata', label: 'Vista raggruppata', sezioneId: 'sez-ordini',
    modulo: constants.MOD_PRO_BASE_GROUP, ruoli: [constants.ROLE_MANAGER, constants.ROLE_OPER]
  },
  {
    id: 'ordini-colonne', label: 'Colonne', sezioneId: 'sez-ordini',
    modulo: constants.MOD_PRO_BASE_COLUMN, ruoli: [constants.ROLE_MANAGER, constants.ROLE_OPER]
  },
  {
    id: 'ordini-dainviare', label: 'Da inviare', sezioneId: 'sez-ordini',
    modulo: constants.MOD_PRO_BASE_UI, ruoli: [constants.ROLE_MANAGER, constants.ROLE_OPER]
  },
  {
    id: 'ordini-inviati', label: 'Inviati', sezioneId: 'sez-ordini',
    modulo: constants.MOD_PRO_BASE_UI, ruoli: [constants.ROLE_MANAGER, constants.ROLE_OPER]
  },
  {
    id: 'ordini-terminati', label: 'Terminati', sezioneId: 'sez-ordini',
    modulo: constants.MOD_PRO_BASE_UI, ruoli: [constants.ROLE_MANAGER, constants.ROLE_OPER]
  },

  // ---- Ricevimenti merce ------------------------------------------------------------------
  {id: 'sez-ricevimenti', label: 'Ricevimenti merce', isSezione: true, configurabile: false, colorClass: 'text-orange-500'},
  {
    id: 'ricevimenti-evasione-ddt', label: 'Evasione DDT da fornitori', sezioneId: 'sez-ricevimenti',
    modulo: constants.MOD_RIC_BASE_UI, ruoli: [constants.ROLE_RICEZIONE]
  },
  {
    id: 'ricevimenti-ddt', label: 'DDT da fornitori', sezioneId: 'sez-ricevimenti',
    modulo: constants.MOD_RIC_BASE_UI, ruoli: [constants.ROLE_RICEZIONE]
  },

  // ---- Conto lavoro -----------------------------------------------------------------------
  {id: 'sez-conto-lavoro', label: 'Conto lavoro', isSezione: true, configurabile: false, colorClass: 'text-blue-300'},
  {
    id: 'conto-lavoro-invio', label: 'Invio materiale a terzista', sezioneId: 'sez-conto-lavoro',
    modulo: constants.MOD_TER_BASE_UI, ruoli: [constants.ROLE_EXTWORK]
  },
  {
    id: 'conto-lavoro-evasione-ddt', label: 'Evasione DDT da terzisti', sezioneId: 'sez-conto-lavoro',
    modulo: constants.MOD_TER_BASE_UI, ruoli: [constants.ROLE_EXTWORK]
  },
  {
    id: 'conto-lavoro-ddt', label: 'DDT da terzisti', sezioneId: 'sez-conto-lavoro',
    modulo: constants.MOD_TER_BASE_UI, ruoli: [constants.ROLE_EXTWORK]
  },

  // ---- Ordini spedizione ------------------------------------------------------------------
  {id: 'sez-spedizioni', label: 'Ordini spedizione', isSezione: true, configurabile: false, colorClass: 'text-orange-800'},
  {
    id: 'spedizioni-imballaggio', label: 'Imballaggio', sezioneId: 'sez-spedizioni',
    modulo: constants.MOD_SPE_BASE_MAN, ruoli: [constants.ROLE_SPE]
  },
  {
    id: 'spedizioni-rilasciate', label: 'Rilasciati', sezioneId: 'sez-spedizioni',
    modulo: constants.MOD_SPE_BASE_MAN, ruoli: [constants.ROLE_SPE]
  },
  {
    id: 'spedizioni-prelevate', label: 'Prelevati', sezioneId: 'sez-spedizioni',
    modulo: constants.MOD_SPE_BASE_MAN, ruoli: [constants.ROLE_SPE]
  },
  {
    id: 'spedizioni-terminate', label: 'Terminati', sezioneId: 'sez-spedizioni',
    modulo: constants.MOD_SPE_BASE_MAN, ruoli: [constants.ROLE_SPE]
  },
  {
    id: 'spedizioni-lista', label: 'Lista spedizioni', sezioneId: 'sez-spedizioni',
    modulo: constants.MOD_SPE_BASE_MAN, ruoli: [constants.ROLE_SPE]
  },

  // ---- Ordini vendita ---------------------------------------------------------------------
  {id: 'sez-vendita', label: 'Ordini vendita', isSezione: true, configurabile: false, colorClass: 'text-blue-500'},
  {
    id: 'vendita-ordini-clienti', label: 'Ordini clienti', sezioneId: 'sez-vendita',
    modulo: constants.MOD_ODV_BASE_UI, ruoli: [constants.ROLE_ODV]
  },

  // ---- Pianificazione ---------------------------------------------------------------------
  {id: 'sez-pianificazione', label: 'Pianificazione', isSezione: true, configurabile: false, colorClass: 'text-fuchsia-500'},
  {
    id: 'pianificazione-consegne', label: 'Pianificazione consegne', sezioneId: 'sez-pianificazione',
    modulo: constants.MOD_PLA_BASE_UI, ruoli: [constants.ROLE_PLANNING]
  },

  // ---- Inventario -------------------------------------------------------------------------
  {id: 'sez-inventario', label: 'Inventario', isSezione: true, configurabile: false, colorClass: 'text-purple-500'},
  {
    id: 'inventario-manuale', label: 'Inventario manuale', sezioneId: 'sez-inventario',
    modulo: constants.MOD_PRO_BASE_INV, ruoli: [constants.ROLE_INV]
  },
  {
    id: 'inventario-pesate', label: 'Inventario pesate', sezioneId: 'sez-inventario',
    modulo: constants.MOD_PRO_BASE_INV, ruoli: [constants.ROLE_INV]
  },

  // ---- Gestione ---------------------------------------------------------------------------
  {id: 'sez-gestione', label: 'Gestione', isSezione: true, configurabile: false, colorClass: 'text-blue-500'},
  {
    id: 'gestione-anagrafiche', label: 'Anagrafiche', sezioneId: 'sez-gestione',
    modulo: constants.MOD_ANA_BASE_UI, ruoli: [constants.ROLE_ANA]
  },
  {
    id: 'gestione-prodotti', label: 'Prodotti', sezioneId: 'sez-gestione',
    modulo: constants.MOD_ART_BASE_UI, ruoli: [constants.ROLE_ART]
  },
  {
    id: 'gestione-risorse', label: 'Risorse', sezioneId: 'sez-gestione',
    modulo: constants.MOD_PRO_BASE_UI, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'gestione-lavorazioni', label: 'Lavorazioni', sezioneId: 'sez-gestione',
    modulo: constants.MOD_PRO_BASE_UI, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'gestione-tracciati', label: 'Tracciati', sezioneId: 'sez-gestione',
    modulo: constants.MOD_PRO_BASE_UI, ruoli: [constants.ROLE_MANAGER]
  },
  // Nessun requisito di licenza: è l'unica voce di Gestione senza check modulo. Comportamento
  // preesistente, mantenuto tale e quale; ora è visibile e correggibile da /licenze.
  {
    id: 'gestione-campi-utente', label: 'Campi utente', sezioneId: 'sez-gestione',
    ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'gestione-causali', label: 'Causali', sezioneId: 'sez-gestione',
    modulo: constants.MOD_PRO_BASE_UI, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'gestione-contenitori', label: 'Contenitori', sezioneId: 'sez-gestione',
    modulo: constants.MOD_PRO_BASE_BOX,
    ruoli: [constants.ROLE_MANAGER, constants.ROLE_RICEZIONE, constants.ROLE_OPER]
  },
  // Il default richiede il modulo WMS; un binding globale puo' sovrascriverlo.
  {
    id: 'gestione-magazzini', label: 'Magazzini', sezioneId: 'sez-gestione',
    modulo: constants.MOD_PRO_BASE_WMS, ruoli: [constants.ROLE_MANAGER]
  },

  // ---- Qualità ----------------------------------------------------------------------------
  {id: 'sez-qualita', label: 'Qualità', isSezione: true, configurabile: false, colorClass: 'text-violet-500'},
  {
    id: 'qualita-statistiche', label: 'Statistiche', sezioneId: 'sez-qualita',
    modulo: constants.MOD_QUA_BASE_UI, ruoli: [constants.ROLE_QUALITY]
  },
  {
    id: 'qualita-controlli', label: 'Controlli qualità', sezioneId: 'sez-qualita',
    modulo: constants.MOD_QUA_BASE_UI, ruoli: [constants.ROLE_QUALITY]
  },
  {
    id: 'qualita-schede-nc', label: 'Schede NC', sezioneId: 'sez-qualita',
    modulo: constants.MOD_QUA_BASE_UI, ruoli: [constants.ROLE_QUALITY]
  },
  {
    id: 'qualita-codici-nc', label: 'Codici NC', sezioneId: 'sez-qualita',
    modulo: constants.MOD_QUA_BASE_UI, ruoli: [constants.ROLE_QUALITY]
  },

  // ---- Analisi ----------------------------------------------------------------------------
  {id: 'sez-analisi', label: 'Analisi', isSezione: true, configurabile: false, colorClass: 'text-pink-500'},
  // ⚠️ SENZA `ruoli`, e non è una dimenticanza (FR-030). Tutte le voci vicine hanno
  // `ruoli: [ROLE_MANAGER]`: copiarle renderebbe il Sinottico INVISIBILE agli account operatore,
  // cioè proprio quelli dei monitor a parete — il primo dei tre contesti d'uso della feature.
  // Resta il requisito di modulo `MOD_PRO_BASE_STA`, e la restrizione per singola organizzazione
  // resta possibile tramite `MenuVociBindings`, che è il meccanismo previsto proprio per questo caso.
  {
    id: 'analisi-sinottico', label: 'Sinottico', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_STA
  },
  {
    id: 'analisi-statistiche', label: 'Statistiche', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_STA, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'analisi-articoli', label: 'Articoli', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_STA, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'analisi-commesse', label: 'Commesse', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_STA, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'analisi-eventi', label: 'Registro eventi', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_STA, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'analisi-sessioni', label: 'Sessioni lavorate', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_STA, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'analisi-movimenti', label: 'Movimenti', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_TRANSFER, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'analisi-movimenti-consumato', label: 'Materiale consumato', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_TRANSFER, ruoli: [constants.ROLE_MANAGER]
  },
  {
    id: 'analisi-pesate', label: 'Pesate', sezioneId: 'sez-analisi',
    modulo: constants.MOD_PRO_BASE_BALANCE, ruoli: [constants.ROLE_MANAGER]
  },

  // ---- Reporter ---------------------------------------------------------------------------
  {id: 'sez-reporter', label: 'Reporter', isSezione: true, configurabile: false, colorClass: 'text-fuchsia-500'},
  {
    id: 'reporter-zpl', label: 'ZPL Editor', sezioneId: 'sez-reporter',
    modulo: constants.MOD_PRO_BASE_PRINTER, ruoli: [constants.ROLE_ADMIN]
  },

  // ---- Amministrazione --------------------------------------------------------------------
  // Non configurabili: governano l'accesso agli strumenti con cui si correggerebbe un errore di
  // configurazione (spegnere "Moduli" renderebbe irraggiungibile il catalogo, spegnere "Mappatura
  // menù" l'editor stesso del legame voce → modulo/ruoli). Configurabile: false qui non è solo un
  // filtro delle UI di configurazione: MenuAccessService.effectiveBinding() lo applica anche a
  // runtime, ignorando qualunque riga di MenuVociBindings per queste voci — vedi il commento lì.
  {id: 'sez-amministrazione', label: 'Amministrazione', isSezione: true, configurabile: false, colorClass: 'text-fuchsia-500'},
  {
    id: 'admin-nuovi-utenti', label: 'Nuovi utenti', sezioneId: 'sez-amministrazione',
    ruoli: [constants.ROLE_ADMIN], configurabile: false
  },
  {
    id: 'admin-operatori', label: 'Operatori', sezioneId: 'sez-amministrazione',
    ruoli: [constants.ROLE_ADMIN, constants.ROLE_MANAGER], configurabile: false
  },
  {
    id: 'admin-organizzazioni', label: 'Organizzazioni', sezioneId: 'sez-amministrazione',
    ruoli: [constants.ROLE_ADMIN], configurabile: false
  },
  {
    id: 'admin-ruoli', label: 'Ruoli', sezioneId: 'sez-amministrazione',
    ruoli: [constants.ROLE_ADMIN], configurabile: false
  },
  {
    id: 'admin-licenze', label: 'Moduli', sezioneId: 'sez-amministrazione',
    ruoli: [constants.ROLE_ADMIN], configurabile: false
  },
  {
    id: 'admin-voci-menu', label: 'Mappatura menù', sezioneId: 'sez-amministrazione',
    ruoli: [constants.ROLE_ADMIN], configurabile: false
  }
] as const satisfies readonly MenuVoce[];

/** Unione degli id esistenti: con strictTemplates un id sbagliato nel template diventa errore di build. */
export type MenuVoceId = (typeof MENU_VOCI_SORGENTE)[number]['id'];

/**
 * Vista tipizzata su cui iterare. `as const` sulla sorgente serve solo a derivare `MenuVoceId`:
 * produce un'unione di tipi letterali su cui le proprietà opzionali non sarebbero accessibili.
 */
export const MENU_VOCI: readonly MenuVoce[] = MENU_VOCI_SORGENTE;

export const MENU_VOCI_BY_ID: Readonly<Record<string, MenuVoce>> =
  MENU_VOCI.reduce<Record<string, MenuVoce>>((acc, voce) => {
    acc[voce.id] = voce;
    return acc;
  }, {});

/** Sole sezioni, nell'ordine di dichiarazione. */
export const MENU_SEZIONI: readonly MenuVoce[] = MENU_VOCI.filter(v => v.isSezione);

/** Voci di una sezione, escluse le sezioni stesse. */
export const vociDiSezione = (sezioneId: string): readonly MenuVoce[] =>
  MENU_VOCI.filter(v => !v.isSezione && v.sezioneId === sezioneId);

/**
 * Voci di una sezione proponibili nelle UI di configurazione (Amministrazione esclusa: governa
 * l'accesso agli stessi strumenti che servirebbero a correggere un errore, vedi il commento sopra
 * sez-amministrazione). Usata sia dal tab per organizzazione sia dall'editor globale, così le due UI
 * restano coerenti fra loro invece di dipendere ciascuna dal ricordarsi il filtro.
 */
export const vociConfigurabiliDiSezione = (sezioneId: string): readonly MenuVoce[] =>
  vociDiSezione(sezioneId).filter(v => v.configurabile !== false);
